import React, { useEffect } from 'react';
import { isIOS, osVersion } from 'react-device-detect';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { themeColor } from '../Theming';
import { Path } from '../Routing';
import images from '../Theming/images';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  trackActivationActionEvent,
  useIsIPad,
} from '../Common/Analytics/utils';
import Header from './components/Header';
import TapHereAnimated from './components/TapHereAnimated';
import { getAnalytics, logEvent } from 'firebase/analytics';
import app from '../Firebase';
import useIncorrectPlatformFallback from './hooks/useIncorrectPlatformFallback';

const Container = styled.div<{ isIPad?: boolean }>`
  flex: 1;
  flex-direction: column;
  display: flex;
  max-height: 100dvh;
  max-width: ${(props) => (props.isIPad ? '100%' : '600px')};
  margin: 0 auto;
`;

const InfoContainer = styled.div<{ isIPad?: boolean }>`
  padding: ${({ isIPad }) => (isIPad ? '32px 200px' : '32px 24px')};
  ${({ isIPad }) =>
    isIPad &&
    `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: left;
  `}
`;

const InfoTitle = styled.h3<{ isIPad?: boolean }>`
  font-weight: 600;
  font-family: Helvetica;
  font-size: ${({ isIPad }) => (isIPad ? '32px' : '22px')};
`;

const ActivationStepsContainer = styled.div<{ isIPad?: boolean }>`
  flex-direction: row;
  display: flex;
  height: 184px;
  width: 100%;
  ${({ isIPad }) =>
    isIPad &&
    `
    justify-content: center;
    align-items: center;
    max-width: 600px;
    margin: 0 auto;
  `}

  & > img {
    height: 100%;
    margin-left: 10px;
    ${({ isIPad }) =>
      isIPad &&
      `
      align-self: flex-start;
    `}
  }

  & > ol {
    padding: 0 0 0 20px;
    margin: 0;
    list-style-type: decimal;
    color: ${themeColor('navyBlueText')};
    font-size: ${({ isIPad }) => (isIPad ? '20px' : '16px')};
    font-weight: 400;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    ${({ isIPad }) =>
      isIPad &&
      `
      padding: 0 0 0px 0px;
      align-self: flex-start;
      height: 85%;
    `}

    & > li {
      padding: 0;
      line-height: 1.5;
      font-family: Helvetica;
      font-weight: 400;
    }
  }
`;

interface Props {
  sliide?: boolean;
}

interface Message {
  data: string & { type: string; givenPermission?: boolean };
}

const NTVPluginActivateMultiStep: React.FC<Props> = () => {
  const history = useHistory();
  const iOS18 = isIOS && parseInt(osVersion) >= 18;

  // Redirect to alternate browser page if not using Safari
  useIncorrectPlatformFallback();

  const infoItem = [
    {
      default: 'Klicken Sie unten auf AA',
      ios18: 'Klicken Sie unten auf das Werkzeugsymbol',
    },
    {
      default: 'Wählen Sie “Erweiterungen verwalten”',
      ios18: 'Wählen Sie “Erweiterungen verwalten”',
    },
    {
      default: 'Aktivieren Sie “ntv Shopping Deals”',
      ios18: 'Aktivieren Sie “ntv Shopping Deals”',
    },
  ];

  const infoItemIpad = [
    {
      default:
        'Klicken Sie auf das Puzzle-Symbol in der Browserleiste und wählen Sie „Erweiterungen verwalten“',
      ios18:
        'Klicken Sie auf das Puzzle-Symbol in der Browserleiste und wählen Sie „Erweiterungen verwalten“',
    },
    {
      default: 'Aktivieren Sie “ntv Shopping Deals”',
      ios18: 'Aktivieren Sie “ntv Shopping Deals”',
    },
  ];
  const query = useQuery();
  const { activation_instructions_page } = ANALYTICS_PAGE_NAMES;
  const { activation_permissions } = ANALYTICS_ACTION_LABELS;
  usePageViewTracking(activation_instructions_page, 'NTV');

  const isIPad = useIsIPad();

  useEffect(() => {
    const analytics = getAnalytics(app);

    const initialHeight = window.innerHeight;
    const interval = setInterval(() => {
      const currentHeight = window.innerHeight;

      if (currentHeight < initialHeight) {
        history.push({
          pathname: Path.NTVPluginActivationAlwaysAllow,
          search: location.search,
        });
      }
    }, 1500);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: Message) => {
      if (message && message.data === '') {
        window.location.reload();
      } else if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            const permissionType = message.data?.givenPermission
              ? 'full'
              : 'partial';
            const previousPermissionType = localStorage.getItem(
              'ntv_permission_type'
            );
            const hasActivatedBefore =
              localStorage.getItem('ntv_plugin_activated') === 'true';

            // Store activation and permission state
            localStorage.setItem('ntv_plugin_activated', 'true');
            localStorage.setItem('ntv_permission_type', permissionType);
            trackActivationActionEvent(
              query,
              'NTV',
              activation_permissions,
              activation_instructions_page,
              {
                permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              }
            );
            logEvent(analytics, 'activation_permissions', {
              permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              partner: 'NTV',
            });

            // Case 1: First time user - default flow
            if (!hasActivatedBefore) {
              if (message.data?.givenPermission) {
                // Full permissions on first activation
                history.push({
                  pathname: Path.NTVPluginActivationStartShopping,
                  search: location.search,
                });
              } else {
                // Partial permissions on first activation - go to AlwaysAllow screen
                history.push({
                  pathname: Path.NTVPluginActivationAlwaysAllow,
                  search: location.search,
                });
              }
              return;
            }

            // Case 2: Extension was disabled, had full permissions, now re-enabled with full
            if (
              previousPermissionType === 'full' &&
              message.data?.givenPermission
            ) {
              history.push({
                pathname: Path.NTVPluginActivationStartShopping,
                search: location.search,
              });
              return;
            }

            // Case 3: Extension was disabled, had partial permissions, now re-enabled with partial
            if (
              previousPermissionType === 'partial' &&
              !message.data?.givenPermission
            ) {
              history.push({
                pathname: Path.NTVPluginActivationPartialPermissions,
                search: location.search,
              });
              return;
            }

            // Fallback: Route based on current permissions
            if (message.data?.givenPermission) {
              history.push({
                pathname: Path.NTVPluginActivationStartShopping,
                search: location.search,
              });
            } else {
              history.push({
                pathname: Path.NTVPluginActivationAlwaysAllow,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      clearInterval(interval);
      window.removeEventListener('message', messageHandler);
    };
  }, [history]);

  return (
    <Container isIPad={isIPad}>
      <Header
        logoWidth={51}
        height={283}
        showFAQ
        text={
          <>
            <span>ntv Shopping Deals</span>
            <span>Jetzt aktivieren</span>
          </>
        }
      />
      {!isIPad && <TapHereAnimated />}
      <InfoContainer isIPad={isIPad}>
        <InfoTitle isIPad={isIPad}>Das müssen Sie dafür tun:</InfoTitle>
        <ActivationStepsContainer isIPad={isIPad}>
          <ol>
            {(isIPad ? infoItemIpad : infoItem).map((item, index) => (
              <li key={index}>{iOS18 ? item.ios18 : item.default}</li>
            ))}
          </ol>
          <img
            src={
              isIPad
                ? images.partnerpluginsdk_safari_activation_steps_ios_ipad
                    .default
                : iOS18
                ? images.partnerpluginsdk_safari_activation_steps_ios_18.default
                : images.partnerpluginsdk_safari_activation_steps.default
            }
            alt="safari activation settings steps"
          />
        </ActivationStepsContainer>
      </InfoContainer>
    </Container>
  );
};

export default NTVPluginActivateMultiStep;
